interface CoordinateResult {
   latitude: number;
   longitude: number;
   source: string;
   original_url?: string;
}

// List of known short URL domains
const SHORT_URL_DOMAINS = [
   'bit.ly', 'goo.gl', 't.co', 'tinyurl.com', 'ow.ly', 'is.gd', 'buff.ly',
   'adc.la', 'bc.vc', 'cli.gs', 'cur.lv', 'db.tt', 'filoops.tw', 'git.io',
   'ht.ly', 'ity.im', 'kck.st', 'lc.chat', 'lnkd.in', 'nyti.ms', 'post.ly',
   'qr.ae', 'qr.net', 's2r.co', 'scrnch.me', 'snipurl.com', 'su.pr', 't2m.io',
   'tr.im', 'twitthis.com', 'v.gd', 'vzturl.com', 'wp.me', 'yep.it', 'zi.ma'
];

/**
 * Main browser function to extract coordinates from a string
 * Will try to extract locally first, and only call server for short URLs
 */
export async function extract_coordinates(input: string): Promise<CoordinateResult | null | 'call_server'> {
   try {
      // First check if it's a short URL that needs server resolution
      if (is_short_url(input)) {
         console.log('Short URL detected, delegating to server');
         return 'call_server'
      }

      // Handle URL content
      if (is_url(input)) {
         const coords = extract_from_url(input);
         if (coords) return { ...coords, original_url: input };

         // Additional check for Google Maps search format
         const search_coords = extract_from_google_search_url(input);
         if (search_coords) return { ...search_coords, original_url: input };
      }

      // Handle as text content as fallback
      return extract_from_text(input);
   } catch (error) {
      console.error('Error extracting coordinates in browser:', error);
      return null;
   }
}

/**
 * Check if a URL is a short URL that needs server resolution
 */
function is_short_url(url: string): boolean {
   try {
      const urlObj = new URL(url);
      console.log({ urlObj })
      console.log('SHORT_URL_DOMAINS: ', SHORT_URL_DOMAINS.some(domain => urlObj.hostname.includes(domain)))
      return SHORT_URL_DOMAINS.some(domain => urlObj.hostname.includes(domain));
   } catch {
      return false;
   }
}

/**
 * Check if text is a valid URL
 */
function is_url(text: string): boolean {
   try {
      new URL(text);
      return true;
   } catch {
      return false;
   }
}

/**
 * Extract coordinates from various map URL formats
 */
function extract_from_url(url: string): CoordinateResult | null {
   // Google Maps patterns 
   const google_maps_patterns = [
      /@(-?\d+\.\d+),(-?\d+\.\d+)/, // @lat,lon
      /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, // !3dlat!4dlon
      /query=(-?\d+\.\d+),(-?\d+\.\d+)/, // query=lat,lon
      /data=(-?\d+\.\d+)!4(-?\d+\.\d+)/, // data=lat!4lon
      /@(-?\d+\.\d+),(-?\d+\.\d+),(\d+\.?\d*)?[a-z]/, // @lat,lon,zoom
   ];

   // Apple Maps patterns
   const apple_maps_patterns = [
      /ll=(-?\d+\.\d+),(-?\d+\.\d+)/, // ll=lat,lon
      /q=(-?\d+\.\d+),(-?\d+\.\d+)/, // q=lat,lon
   ];

   // OpenStreetMap patterns
   const osm_patterns = [
      /#map=\d+\/(-?\d+\.\d+)\/(-?\d+\.\d+)/, // #map=zoom/lat/lon
      /mlat=(-?\d+\.\d+)&mlon=(-?\d+\.\d+)/, // mlat=lat&mlon=lon
   ];

   // Bing Maps patterns
   const bing_maps_patterns = [
      /cp=(-?\d+\.\d+)~(-?\d+\.\d+)/, // cp=lat~lon
   ];

   const all_patterns = [
      ...google_maps_patterns,
      ...apple_maps_patterns,
      ...osm_patterns,
      ...bing_maps_patterns
   ];

   for (const pattern of all_patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[2]) {
         return {
            latitude: parseFloat(match[1]),
            longitude: parseFloat(match[2]),
            source: 'map_url'
         };
      }
   }

   return null;
}

/**
 * Extract coordinates from Google Maps search URLs
 * Example: https://www.google.com/maps/search/32.884991,+13.199751
 */
function extract_from_google_search_url(url: string): CoordinateResult | null {
   const search_patterns = [
      /\/search\/([-+]?\d+\.\d+),([-+]?\d+\.\d+)/,
      /\/place\/([-+]?\d+\.\d+),([-+]?\d+\.\d+)/,
      /\/dir\/([-+]?\d+\.\d+),([-+]?\d+\.\d+)/,
   ];

   for (const pattern of search_patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[2]) {
         return {
            latitude: parseFloat(match[1]),
            longitude: parseFloat(match[2]),
            source: 'google_search_url'
         };
      }
   }

   return null;
}

/**
 * Fallback method to extract coordinates from plain text
 */
function extract_from_text(text: string): CoordinateResult | null {
   // Common coordinate patterns
   const coordinate_patterns = [
      /(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/, // lat, lon
      /(-?\d+\.\d+)\s*[\s°]\s*(-?\d+\.\d+)/, // lat ° lon
      /lat(?:itude)?:\s*(-?\d+\.\d+)[ ,]\s*lon(?:gitude)?:\s*(-?\d+\.\d+)/i, // lat: 12.34, lon: 56.78
   ];

   for (const pattern of coordinate_patterns) {
      const match = text.match(pattern);
      if (match && match[1] && match[2]) {
         return {
            latitude: parseFloat(match[1]),
            longitude: parseFloat(match[2]),
            source: 'text'
         };
      }
   }

   return null;
}
